import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, DollarSignIcon, CheckCircleIcon, EuroIcon } from 'lucide-react';
interface CommissionCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    ownerCommissionAmount?: number;
    clientCommissionAmount?: number;
    totalCommission: number;
    notes: string;
  }) => void;
  viewing: any;
}
export function CommissionCollectionModal({
  isOpen,
  onClose,
  onSubmit,
  viewing
}: CommissionCollectionModalProps) {
  const [ownerCommissionAmount, setOwnerCommissionAmount] = useState<number>(0);
  const [clientCommissionAmount, setClientCommissionAmount] = useState<number>(0);
  const [notes, setNotes] = useState('');
  // Calculate expected commission based on property price and percentages
  const calculateExpectedCommission = (percentage: number, vatOption: string) => {
    if (!viewing?.propertyPrice || !percentage) return 0;
    const baseCommission = viewing.propertyPrice * percentage / 100;
    if (vatOption === 'additional') {
      return baseCommission * 1.21; // Add 21% VAT
    }
    return baseCommission; // VAT included
  };
  const expectedOwnerCommission = viewing?.commissionPaidBy === 'owner' || viewing?.commissionPaidBy === 'both' ? calculateExpectedCommission(viewing?.ownerCommission || 0, viewing?.ownerVatOption || 'included') : 0;
  const expectedClientCommission = viewing?.commissionPaidBy === 'client' || viewing?.commissionPaidBy === 'both' ? calculateExpectedCommission(viewing?.clientCommission || 0, viewing?.clientVatOption || 'included') : 0;
  const totalCommission = ownerCommissionAmount + clientCommissionAmount;
  const handleSubmit = () => {
    if ((viewing?.commissionPaidBy === 'owner' || viewing?.commissionPaidBy === 'both') && ownerCommissionAmount <= 0) {
      alert('Te rog introdu suma comisionului de la proprietar');
      return;
    }
    if ((viewing?.commissionPaidBy === 'client' || viewing?.commissionPaidBy === 'both') && clientCommissionAmount <= 0) {
      alert('Te rog introdu suma comisionului de la client');
      return;
    }
    onSubmit({
      ownerCommissionAmount: viewing?.commissionPaidBy === 'owner' || viewing?.commissionPaidBy === 'both' ? ownerCommissionAmount : undefined,
      clientCommissionAmount: viewing?.commissionPaidBy === 'client' || viewing?.commissionPaidBy === 'both' ? clientCommissionAmount : undefined,
      totalCommission,
      notes
    });
    // Reset
    setOwnerCommissionAmount(0);
    setClientCommissionAmount(0);
    setNotes('');
  };
  return <AnimatePresence data-id="element-2971">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-2972" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2973">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" data-id="element-2974">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white px-6 py-5" data-id="element-2975">
                <div className="flex items-center justify-between" data-id="element-2976">
                  <div className="flex items-center space-x-3" data-id="element-2977">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-2978">
                      <DollarSignIcon className="w-6 h-6" data-id="element-2979" />
                    </div>
                    <div data-id="element-2980">
                      <h3 className="text-xl font-bold" data-id="element-2981">Comision Încasat</h3>
                      <p className="text-sm text-emerald-100" data-id="element-2982">
                        {viewing?.clientName} - {viewing?.propertyId}
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-2983">
                    <XIcon className="w-6 h-6" data-id="element-2984" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4" data-id="element-2985">
                {/* Owner Commission */}
                {(viewing?.commissionPaidBy === 'owner' || viewing?.commissionPaidBy === 'both') && <div className="bg-purple-50 rounded-xl p-4 border border-purple-200" data-id="element-2986">
                    <label className="block text-sm font-medium text-purple-900 mb-2" data-id="element-2987">
                      Comision Proprietar (EUR) *
                    </label>
                    <div className="relative" data-id="element-2988">
                      <EuroIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" data-id="element-2989" />
                      <input type="number" step="0.01" min="0" value={ownerCommissionAmount || ''} onChange={e => setOwnerCommissionAmount(parseFloat(e.target.value) || 0)} className="w-full pl-10 pr-4 py-3 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white" placeholder="0.00" data-id="element-2990" />
                    </div>
                    <div className="mt-2 space-y-1" data-id="element-2991">
                      <p className="text-xs text-purple-700" data-id="element-2992">
                        Procent: {viewing?.ownerCommission}%
                      </p>
                      <p className="text-xs text-purple-700 font-bold" data-id="element-2993">
                        TVA:{' '}
                        {viewing?.ownerVatOption === 'additional' ? 'Se adaugă (21%)' : 'Inclus în procent'}
                      </p>
                      <p className="text-xs text-purple-600" data-id="element-2994">
                        Estimat: {expectedOwnerCommission.toFixed(2)} EUR
                      </p>
                    </div>
                  </div>}

                {/* Client Commission */}
                {(viewing?.commissionPaidBy === 'client' || viewing?.commissionPaidBy === 'both') && <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-2995">
                    <label className="block text-sm font-medium text-blue-900 mb-2" data-id="element-2996">
                      Comision Client (EUR) *
                    </label>
                    <div className="relative" data-id="element-2997">
                      <EuroIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400" data-id="element-2998" />
                      <input type="number" step="0.01" min="0" value={clientCommissionAmount || ''} onChange={e => setClientCommissionAmount(parseFloat(e.target.value) || 0)} className="w-full pl-10 pr-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white" placeholder="0.00" data-id="element-2999" />
                    </div>
                    <div className="mt-2 space-y-1" data-id="element-3000">
                      <p className="text-xs text-blue-700" data-id="element-3001">
                        Procent: {viewing?.clientCommission}%
                      </p>
                      <p className="text-xs text-blue-700 font-bold" data-id="element-3002">
                        TVA:{' '}
                        {viewing?.clientVatOption === 'additional' ? 'Se adaugă (21%)' : 'Inclus în procent'}
                      </p>
                      <p className="text-xs text-blue-600" data-id="element-3003">
                        Estimat: {expectedClientCommission.toFixed(2)} EUR
                      </p>
                    </div>
                  </div>}

                {/* Total */}
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border-2 border-emerald-300" data-id="element-3004">
                  <div className="flex items-center justify-between" data-id="element-3005">
                    <span className="text-sm font-bold text-emerald-900" data-id="element-3006">
                      Total Comision:
                    </span>
                    <span className="text-2xl font-bold text-emerald-600" data-id="element-3007">
                      {totalCommission.toFixed(2)} EUR
                    </span>
                  </div>
                </div>

                {/* Notes */}
                <div data-id="element-3008">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3009">
                    Observații (opțional)
                  </label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="ex: Plată efectuată prin transfer bancar. Factură emisă..." className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none" data-id="element-3010" />
                </div>

                {/* Success Info */}
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200" data-id="element-3011">
                  <div className="flex items-start space-x-3" data-id="element-3012">
                    <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-3013" />
                    <div data-id="element-3014">
                      <p className="text-sm font-bold text-emerald-900 mb-1" data-id="element-3015">
                        Finalizare Tranzacție
                      </p>
                      <p className="text-sm text-emerald-700" data-id="element-3016">
                        După confirmarea încasării comisionului, tranzacția va
                        fi marcată ca finalizată și va apărea în rapoartele de
                        vânzări.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 px-6 py-4 flex items-center justify-between border-t border-slate-200" data-id="element-3017">
                <button onClick={onClose} className="px-5 py-2.5 rounded-xl font-semibold text-slate-700 hover:bg-slate-200 transition-colors" data-id="element-3018">
                  Anulează
                </button>
                <button onClick={handleSubmit} className="px-6 py-2.5 rounded-xl font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:scale-105 transition-all flex items-center space-x-2" data-id="element-3019">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-3020" />
                  <span data-id="element-3021">Confirmă Încasare</span>
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}